import React, { useEffect, useState, useRef, useMemo } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError, rupees } from "@/lib/utils";
import { Plus, X, UploadCloud, AlertCircle } from "lucide-react";

const PURITIES = ["18KT", "14KT", "9KT", "PT950", "925"];

// CS 16 — Resolve a product's current source into a composite "type:id" value
// matching the Upload dropdown convention.
function productToSourceComposite(p, franchises, distributors) {
  if (p.source_type === "ho") return "ho:none";
  if (p.source_type === "unassigned") return "unassigned:none";
  if (p.source_type === "franchise" && p.source_id) return `franchise:${p.source_id}`;
  if (p.source_type === "distributor" && p.source_id) return `distributor:${p.source_id}`;
  // Legacy fallback: source_tag is a name — look up
  const tag = p.source_tag || "";
  if (tag === "Head Office Stock") return "ho:none";
  if (tag === "Unassigned" || !tag) return "unassigned:none";
  const f = franchises.find((x) => x.name === tag);
  if (f) return `franchise:${f.id}`;
  const d = distributors.find((x) => x.name === tag);
  if (d) return `distributor:${d.id}`;
  return "unassigned:none";
}

export default function ProductEditModal({ jewelCode, open, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [shapes, setShapes] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [livePricing, setLivePricing] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const fileRef = useRef();
  const previewTimer = useRef(null);

  useEffect(() => {
    if (!open || !jewelCode) return;
    setData(null);
    setLivePricing(null);
    setPhotoFile(null);
    Promise.all([
      api.get(`/products/${encodeURIComponent(jewelCode)}`),
      api.get("/diamond-shapes"),
      api.get("/franchises"),
      api.get("/distributors").catch(() => ({ data: [] })),
    ]).then(([prod, sh, fr, di]) => {
      setData(prod.data);
      const p = prod.data.product;
      const composite = productToSourceComposite(p, fr.data || [], di.data || []);
      setForm({
        jewel_code: p.jewel_code,
        style_no: p.style_no || "",
        category: p.category || "",
        sub_category: p.sub_category || "",
        item_code: p.item_code || "",
        gross_weight: p.gross_weight ?? "",
        net_weight: p.net_weight ?? "",
        purity: p.purity || "14KT",
        source_composite: composite,
        is_memo: !!p.is_memo,
        // CS 21.54 -- explicit tri-state: null = auto-detect via AL prefix,
        // true = force ASHTH LITE on, false = force off. Represented in the
        // form as a select with values "auto" | "on" | "off" for clarity.
        is_ashth_lite: p.is_ashth_lite === true ? "on"
                      : p.is_ashth_lite === false ? "off"
                      : "auto",
        diamond_lines: (p.diamond_lines || []).map((l, idx) => ({ _uid: `init-${idx}-${Date.now()}`, shape_code: l.shape_code, pieces: l.pieces, weight_ct: l.weight_ct })),
      });
      setShapes(sh.data.filter((s) => s.is_active));
      setFranchises(fr.data || []);
      setDistributors(di.data || []);
      setPhotoUrl(p.photo_url || "");
      setPhotoPreview(p.photo_url || "");
      setLivePricing(prod.data.pricing);
    }).catch(() => {
      toast.error("Failed to load product");
      onClose?.();
    });
  }, [open, jewelCode, onClose]);

  // Validation
  const errors = useMemo(() => {
    if (!form) return {};
    const e = {};
    if (!form.jewel_code?.trim()) e.jewel_code = "Required";
    if (!form.category?.trim()) e.category = "Required";
    if (!form.item_code?.trim()) e.item_code = "Required";
    const nw = parseFloat(form.net_weight);
    if (!nw || nw <= 0) e.net_weight = "Must be > 0";
    const gw = parseFloat(form.gross_weight);
    if (form.gross_weight !== "" && form.gross_weight !== null && (isNaN(gw) || gw < 0)) e.gross_weight = "Invalid";
    if (gw && nw && gw < nw) e.gross_weight = "Gross must be ≥ Net";
    if (!form.source_composite) e.source_composite = "Required";
    if (!form.purity) e.purity = "Required";
    form.diamond_lines.forEach((l, i) => {
      const p = parseInt(l.pieces);
      const w = parseFloat(l.weight_ct);
      if (!l.shape_code) e[`line_${i}_shape`] = "Pick a shape";
      if (!p || p <= 0) e[`line_${i}_pcs`] = ">0";
      if (!w || w <= 0) e[`line_${i}_wt`] = ">0";
    });
    return e;
  }, [form]);

  const isValid = Object.keys(errors).length === 0;

  const addLine = () => setForm({ ...form, diamond_lines: [...form.diamond_lines, { _uid: `add-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, shape_code: shapes[0]?.short_code || "RD", pieces: 1, weight_ct: 0.1 }] });
  const removeLine = (i) => setForm({ ...form, diamond_lines: form.diamond_lines.filter((_, idx) => idx !== i) });
  const updateLine = (i, field, value) => {
    const lines = [...form.diamond_lines];
    lines[i] = { ...lines[i], [field]: value };
    setForm({ ...form, diamond_lines: lines });
  };

  // Live preview debounced
  useEffect(() => {
    if (!form || !isValid) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      setPreviewBusy(true);
      try {
        // CS 21.57 -- Include the jewel_code + tri-state ASHTH LITE toggle
        // in the preview payload so the modal's Live Price Preview reflects
        // the toggle state on every click (including ON -> Auto and
        // OFF -> Auto), not just the initial server-side value.
        //   "on"   -> true  (Force ON)
        //   "off"  -> false (Force OFF)
        //   "auto" -> null  (Auto; server does prefix match on jewel_code)
        const alPreview = form.is_ashth_lite === "on" ? true
                       : form.is_ashth_lite === "off" ? false
                       : null;
        const payload = {
          jewel_code: form.jewel_code || null,
          is_ashth_lite: alPreview,
          purity: form.purity,
          net_weight: parseFloat(form.net_weight) || 0,
          gross_weight: parseFloat(form.gross_weight) || 0,
          diamond_lines: form.diamond_lines.map((l) => ({
            shape_code: l.shape_code,
            pieces: parseInt(l.pieces) || 0,
            weight_ct: parseFloat(l.weight_ct) || 0,
          })),
        };
        const { data } = await api.post("/products/preview-pricing", payload);
        setLivePricing(data.pricing);
      } catch {
        // keep last good preview (live re-pricing is best-effort)
      } finally {
        setPreviewBusy(false);
      }
    }, 400);
    return () => previewTimer.current && clearTimeout(previewTimer.current);
  }, [form, isValid]);

  const onPhotoFile = (f) => {
    setPhotoFile(f);
    if (f) {
      const url = URL.createObjectURL(f);
      setPhotoPreview(url);
    }
  };

  const uploadPhoto = async () => {
    if (photoFile) {
      const fd = new FormData();
      fd.append("jewel_code", form.jewel_code);
      fd.append("file", photoFile);
      const { data } = await api.post("/photos/single", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.photo_url;
    }
    if (photoUrl !== (data?.product?.photo_url || "")) {
      await api.post("/photos/url", { jewel_code: form.jewel_code, photo_url: photoUrl });
      return photoUrl;
    }
    return null;
  };

  const save = async () => {
    if (!isValid) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSaving(true);
    try {
      if (photoFile || photoUrl !== (data?.product?.photo_url || "")) {
        await uploadPhoto();
      }
      // Parse composite source back into source_type + source_id
      const [stype, sidRaw] = (form.source_composite || "unassigned:none").split(":");
      const source_id = sidRaw === "none" ? null : sidRaw;
      const { source_composite, is_ashth_lite: alTri, ...rest } = form;
      // CS 21.54 -- Convert tri-state to Optional[bool] for the API:
      //   "auto" -> null (server auto-detects via AL prefix)
      //   "on"   -> true (force ASHTH LITE, uses distribution rates)
      //   "off"  -> false (force off, uses franchise rates)
      const al_payload_value = alTri === "on" ? true : alTri === "off" ? false : null;
      const payload = {
        ...rest,
        source_type: stype,
        source_id,
        is_ashth_lite: al_payload_value,
        gross_weight: parseFloat(form.gross_weight) || 0,
        net_weight: parseFloat(form.net_weight) || 0,
        diamond_lines: form.diamond_lines.map((l) => ({
          shape_code: l.shape_code,
          pieces: parseInt(l.pieces) || 0,
          weight_ct: parseFloat(l.weight_ct) || 0,
        })),
      };
      await api.patch(`/products/${encodeURIComponent(jewelCode)}`, payload);
      toast.success("Product updated");
      onSaved?.(payload.jewel_code || jewelCode);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-white max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="product-edit-modal">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Edit Product</DialogTitle></DialogHeader>
        {!form ? (
          <div className="space-y-3"><div className="skeleton h-12" /><div className="skeleton h-32" /></div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Basic */}
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C] mb-3">Basic details</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FieldI label="Jewel Code *" value={form.jewel_code} onChange={(v) => setForm({ ...form, jewel_code: v })} testid="edit-jewel-code" error={errors.jewel_code} />
                  <FieldI label="Style No" value={form.style_no} onChange={(v) => setForm({ ...form, style_no: v })} />
                  <FieldI label="Category *" value={form.category} onChange={(v) => setForm({ ...form, category: v })} error={errors.category} />
                  <FieldI label="Sub-category" value={form.sub_category} onChange={(v) => setForm({ ...form, sub_category: v })} />
                  <FieldI label="Item Code *" value={form.item_code} onChange={(v) => setForm({ ...form, item_code: v })} error={errors.item_code} />
                  <FieldS label="Purity *" value={form.purity} onChange={(v) => setForm({ ...form, purity: v })} options={PURITIES.map((p) => ({ value: p, label: p }))} testid="edit-purity" error={errors.purity} />
                  <FieldI label="Gross Weight (g)" type="number" value={form.gross_weight} onChange={(v) => setForm({ ...form, gross_weight: v })} step="0.001" error={errors.gross_weight} />
                  <FieldI label="Net Weight (g) *" type="number" value={form.net_weight} onChange={(v) => setForm({ ...form, net_weight: v })} step="0.001" testid="edit-net-weight" error={errors.net_weight} />
                  <div className="md:col-span-2">
                    <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Source *</label>
                    <Select value={form.source_composite || ""} onValueChange={(v) => setForm({ ...form, source_composite: v })}>
                      <SelectTrigger data-testid="edit-source" className={errors.source_composite ? "border-red-400" : ""}><SelectValue placeholder="Choose a source…" /></SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">Head Office</SelectLabel>
                          <SelectItem value="ho:none">Head Office Stock</SelectItem>
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">Franchises</SelectLabel>
                          {franchises.map((f) => <SelectItem key={`f-${f.id}`} value={`franchise:${f.id}`}>{f.name}</SelectItem>)}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">Distributors</SelectLabel>
                          {distributors.map((d) => <SelectItem key={`d-${d.id}`} value={`distributor:${d.id}`}>{d.name}</SelectItem>)}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">Other</SelectLabel>
                          <SelectItem value="unassigned:none">Unassigned</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-slate-400 mt-1">Source determines which catalogue this product appears in.</p>
                    {errors.source_composite && <p className="text-[10px] text-red-600 mt-1">{errors.source_composite}</p>}
                  </div>
                </div>
                <div className="mt-4 p-3 rounded-md border border-[#0D1B2A]/10 bg-[#FAF8F3]" data-testid="edit-memo-row">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!form.is_memo}
                      onChange={(e) => setForm({ ...form, is_memo: e.target.checked })}
                      data-testid="edit-memo-toggle"
                      className="mt-1 w-4 h-4 rounded border-[#0D1B2A]/30 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div>
                      <div className="text-sm font-medium text-[#0D1B2A]">Memo (Consignment) Status</div>
                      <p className="text-xs text-slate-500 mt-1">Turn ON if this product is on consignment with the franchise. Turn OFF if the product has been sold.</p>
                    </div>
                  </label>
                </div>
                {/* CS 21.54 -- ASHTH LITE manual toggle. Tri-state:
                    auto (default), Force ON, Force OFF. Auto uses the
                    strict AL- prefix rule; manual settings win over auto. */}
                <div className="mt-4 p-3 rounded-md border border-[#0D1B2A]/10 bg-[#FAF8F3]" data-testid="edit-ashth-lite-row">
                  <div className="flex items-start gap-3">
                    <div className="mt-1 w-4 h-4 rounded-full bg-[#C9A84C]" aria-hidden />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-[#0D1B2A]">ASHTH LITE (AL-series)</div>
                      <p className="text-xs text-slate-500 mt-1">
                        Franchise pricing of AL products routes through Distribution Rate Settings.
                        Auto-detects when the product code starts with <code className="font-mono">AL-</code> or <code className="font-mono">AL&nbsp;</code>.
                        Use Force ON / OFF to override.
                      </p>
                      <div className="mt-2 flex gap-1.5" role="radiogroup" aria-label="ASHTH LITE override">
                        {[
                          { v: "auto", label: "Auto (by prefix)" },
                          { v: "on", label: "Force ON" },
                          { v: "off", label: "Force OFF" },
                        ].map((o) => (
                          <button
                            key={o.v}
                            type="button"
                            role="radio"
                            aria-checked={form.is_ashth_lite === o.v}
                            onClick={() => setForm({ ...form, is_ashth_lite: o.v })}
                            data-testid={`edit-ashth-lite-${o.v}`}
                            className={`px-2.5 py-1 text-[11px] rounded-full border transition ${
                              form.is_ashth_lite === o.v
                                ? "bg-[#0D1B2A] text-white border-[#0D1B2A]"
                                : "bg-white text-[#0D1B2A] border-[#0D1B2A]/20 hover:bg-[#0D1B2A]/5"
                            }`}
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Diamond Lines */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">Diamond Lines</p>
                  <Button onClick={addLine} variant="outline" size="sm" className="border-[#0D1B2A]/15" data-testid="add-line-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add line
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.diamond_lines.length === 0 && (
                    <p className="text-xs text-slate-400 italic">No diamond lines.</p>
                  )}
                  {form.diamond_lines.map((l, i) => (
                    <div key={l._uid || i} className="grid grid-cols-12 gap-2 items-start" data-testid={`diamond-line-${i}`}>
                      <div className="col-span-1 text-xs text-slate-400 text-center pt-2">{i + 1}</div>
                      <div className="col-span-4">
                        <Select value={l.shape_code} onValueChange={(v) => updateLine(i, "shape_code", v)}>
                          <SelectTrigger className={errors[`line_${i}_shape`] ? "border-red-400" : ""} data-testid={`line-shape-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{shapes.map((s) => <SelectItem key={s.short_code} value={s.short_code}>{s.short_code} · {s.display_name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3">
                        <Input type="number" value={l.pieces} onChange={(e) => updateLine(i, "pieces", e.target.value)} className={errors[`line_${i}_pcs`] ? "border-red-400" : ""} placeholder="Pcs" data-testid={`line-pcs-${i}`} />
                      </div>
                      <div className="col-span-3">
                        <Input type="number" step="0.001" value={l.weight_ct} onChange={(e) => updateLine(i, "weight_ct", e.target.value)} className={errors[`line_${i}_wt`] ? "border-red-400" : ""} placeholder="Wt ct" />
                      </div>
                      <button onClick={() => removeLine(i)} className="col-span-1 p-2 text-red-500 hover:bg-red-50 rounded-md flex justify-center" data-testid={`remove-line-${i}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Photo */}
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C] mb-3">Product Photo</p>
                <div className="grid md:grid-cols-3 gap-3 items-start">
                  <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-[#0D1B2A]/15 rounded-md p-5 text-center cursor-pointer hover:border-[#C9A84C]">
                    <UploadCloud className="w-7 h-7 mx-auto text-[#C9A84C] mb-2" strokeWidth={1.5} />
                    <p className="text-xs text-slate-600">{photoFile ? photoFile.name : "Upload image"}</p>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPhotoFile(e.target.files[0])} data-testid="edit-photo-file" />
                  </div>
                  <FieldI label="Or paste URL" value={photoUrl} onChange={(v) => { setPhotoUrl(v); setPhotoPreview(v); setPhotoFile(null); }} placeholder="https://…" testid="edit-photo-url" />
                  {photoPreview ? (
                    <img src={photoPreview} alt="" className="w-24 h-24 object-cover rounded border border-[#0D1B2A]/10" data-testid="edit-photo-preview" />
                  ) : (
                    <div className="w-24 h-24 rounded bg-slate-50 border border-dashed border-[#0D1B2A]/10 flex items-center justify-center text-[10px] text-slate-400">No photo</div>
                  )}
                </div>
              </div>
            </div>

            {/* Live Pricing Preview */}
            <aside className="lg:sticky lg:top-0 self-start">
              <div className="luxe-card p-5 bg-[#FAF8F3]" data-testid="live-price-preview">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">Live Price Preview</p>
                  {previewBusy && <span className="text-[10px] text-slate-400">computing…</span>}
                </div>
                {!isValid ? (
                  <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>Fix the highlighted fields to refresh preview.</div>
                  </div>
                ) : livePricing ? (
                  <div className="space-y-3 text-sm">
                    <PriceRow label="Metal cost" value={livePricing.metal_cost} muted />
                    <PriceRow label="Diamond cost" value={livePricing.diamond?.total_cost} muted />
                    <div className="h-px bg-[#0D1B2A]/10 my-2" />
                    {livePricing.company && (
                      <PriceRow label="Company total" value={livePricing.company.total} bold tone="navy" testid="preview-company" />
                    )}
                    {livePricing.franchise && (
                      <PriceRow label="Franchise total" value={livePricing.franchise.total} bold tone="gold" testid="preview-franchise" />
                    )}
                    {livePricing.customer && (
                      <PriceRow label="Customer MRP" value={livePricing.customer.total} bold tone="emerald" testid="preview-customer" big />
                    )}
                    {livePricing.gp && (
                      <div className="text-[10px] text-slate-500 pt-2 border-t border-[#0D1B2A]/5">
                        GP Co {livePricing.gp.company_gp_pct?.toFixed(1)}% · Fr {livePricing.gp.franchise_gp_pct?.toFixed(1)}%
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-slate-400">No preview yet.</div>
                )}
              </div>
              {Object.keys(errors).length > 0 && (
                <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="validation-summary">
                  <div className="font-medium mb-1">{Object.keys(errors).length} validation issue{Object.keys(errors).length === 1 ? "" : "s"}</div>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {Object.entries(errors).slice(0, 5).map(([k, v]) => <li key={k}>{k.replace(/_/g, " ")}: {v}</li>)}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !isValid} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white disabled:opacity-50" data-testid="edit-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PriceRow({ label, value, muted, bold, tone, big, testid }) {
  if (value === undefined || value === null) return null;
  const toneCls = tone === "navy" ? "text-[#0D1B2A]" : tone === "gold" ? "text-[#C9A84C]" : tone === "emerald" ? "text-emerald-700" : "text-slate-700";
  return (
    <div className="flex items-center justify-between">
      <span className={`${muted ? "text-slate-500" : "text-slate-700"} text-xs uppercase tracking-wider`}>{label}</span>
      <span className={`tabular-nums ${bold ? "font-semibold" : ""} ${big ? "text-lg" : ""} ${toneCls}`} data-testid={testid}>
        {rupees(value || 0)}
      </span>
    </div>
  );
}

function FieldI({ label, value, onChange, type = "text", step, placeholder, testid, error }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">{label}</label>
      <Input type={type} step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className={error ? "border-red-400 focus-visible:ring-red-300" : ""} />
      {error && <p className="text-[10px] text-red-600 mt-1">{error}</p>}
    </div>
  );
}

function FieldS({ label, value, onChange, options, testid, error }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">{label}</label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className={error ? "border-red-400" : ""}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
      {error && <p className="text-[10px] text-red-600 mt-1">{error}</p>}
    </div>
  );
}
